import type { CSSProperties, ReactNode } from "react";

export type SummaryCard = {
  key: string;
  label: string;
  value: string;
  /** Small second line beside the value, e.g. "of 3,746". */
  sub?: string;
  icon: ReactNode;
  /** The figure's own map colour (field data orange, submitted green ...),
   *  shown as the card's tick so the strip reads like the map's legend. */
  color: string;
};

/**
 * The row of headline figures above the map, styled as a legend strip: white
 * sheets on hairlines, each ticked in the colour its figure has on the map.
 * Follows the location filter. Rises in on load, staggered.
 */
export default function SummaryCards({ cards }: { cards: SummaryCard[] }) {
  return (
    <ul className="thin-scroll flex gap-2 overflow-x-auto pb-0.5" aria-label="Summary">
      {cards.map((c, i) => (
        <li
          key={c.key}
          className="reveal lift relative flex min-w-[10rem] flex-1 items-center gap-3 overflow-hidden rounded-md border border-line bg-surface px-3 py-2.5"
          style={{ "--i": i } as CSSProperties}
        >
          <span className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: c.color }} aria-hidden="true" />
          <span
            className="flex h-8 w-8 flex-none items-center justify-center rounded-full border"
            style={{ color: c.color, borderColor: `${c.color}55`, backgroundColor: `${c.color}12` }}
            aria-hidden="true"
          >
            {c.icon}
          </span>
          <div className="min-w-0">
            <p className="truncate font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
              {c.label}
            </p>
            <p className="font-display text-[26px] font-bold leading-none tabular-nums text-ink">
              {c.value}
              {c.sub && <span className="ml-1.5 font-sans text-2xs font-medium text-ink-muted">{c.sub}</span>}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}
